import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Inbox, Search, RefreshCw, FileCheck2, X, Trash2, DollarSign,
  Loader2, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { api, getUser, isDemoWorkspace, type Discovered, type DiscoveredEstimate, type DiscoveredFilters, type DiscoveredSortField, type Site } from "../api";
import { Pagination } from "../components/Pagination";
import { SortHeader, type SortOrder } from "../components/SortHeader";
import { useConfirm } from "../components/Modal";
import { Alert, PageHeader } from "../components/Page";

const STATUS_TABS: { key: NonNullable<DiscoveredFilters["status_filter"]>; label: string }[] = [
  { key: "discovered", label: "Discovered" },
  { key: "selected", label: "Queued" },
  { key: "converted", label: "Converted" },
  { key: "ignored", label: "Ignored" },
  { key: "all", label: "All" },
];

export function DiscoveredPage() {
  const user = getUser();
  const isSuper = user?.role === "superadmin";
  const demo = isDemoWorkspace();
  const [rows, setRows] = useState<Discovered[]>([]);
  const [total, setTotal] = useState(0);
  const [sites, setSites] = useState<Site[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<DiscoveredFilters>({ status_filter: "discovered" });
  const [search, setSearch] = useState("");
  // Pagination + sort. Reset to page 0 whenever filters/sort change.
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [sort, setSort] = useState<DiscoveredSortField>("discovered_at");
  const [order, setOrder] = useState<SortOrder>("desc");

  const refresh = useCallback(async () => {
    try {
      const [data, c] = await Promise.all([
        api.listDiscovered({ ...filters, sort, order, limit: perPage, offset: page * perPage }),
        api.discoveredCounts(),
      ]);
      setRows(data.items);
      setTotal(data.total);
      setCounts(c);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [filters, sort, order, page, perPage]);

  // Reset to first page whenever filters or sort change — otherwise users
  // who're on page 7 of "All" land on an empty page 7 after switching to a
  // tab with only 30 rows.
  useEffect(() => { setPage(0); }, [filters, sort, order, perPage]);

  function onSort(field: DiscoveredSortField, nextOrder: SortOrder) {
    setSort(field); setOrder(nextOrder);
  }

  useEffect(() => { api.listSites().then(setSites).catch(() => {}); }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Poll while there are 'selected' rows (worker downloading them) OR any
  // row on the current page is still being scanned by sdoc.scan_discovered_pdf.
  useEffect(() => {
    const polling = (
      (filters.status_filter === "selected" && rows.some((r) => r.status === "selected"))
      || rows.some((r) => r.scan_status === "pending")
    );
    if (!polling) return;
    const t = window.setInterval(refresh, 3000);
    return () => window.clearInterval(t);
  }, [filters.status_filter, rows, refresh]);

  function setStatus(s: NonNullable<DiscoveredFilters["status_filter"]>) {
    setFilters((f) => ({ ...f, status_filter: s }));
    setSelected(new Set());
  }

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setFilters((f) => ({ ...f, q: search || undefined }));
  }

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  }
  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Takes explicit ids: the per-row button used to set the selection and
  // then read it in the same tick (stale state), converting nothing — or
  // whatever was ticked before.
  async function convertIds(ids: string[]) {
    if (ids.length === 0) return;
    try {
      await api.convertDiscovered(ids);
      setSelected(new Set());
      await refresh();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }
  async function convertSelected() {
    await convertIds(Array.from(selected));
  }
  async function ignoreSelected() {
    if (selected.size === 0) return;
    try {
      await api.ignoreDiscovered(Array.from(selected));
      setSelected(new Set());
      await refresh();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }
  const [confirm, confirmNode] = useConfirm();

  async function deleteRow(id: string, filename: string) {
    const ok = await confirm({
      title: "Delete this discovery?",
      message: <>Removes <strong>{filename}</strong> from your list. It won't be crawled or converted again unless you re-add it.</>,
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try { await api.deleteDiscovered(id); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }

  const siteName = useMemo(() => {
    const m = new Map(sites.map((s) => [s.id, s.domain]));
    return (id: string) => m.get(id) || id.slice(0, 8);
  }, [sites]);

  // Bulk estimate — recomputed whenever the selection changes. Authoritative
  // server-side estimate (covers gaps like unknown content-length) is fetched
  // on selection change; while the request is in-flight we fall back to a
  // client-side sum so the bar reacts instantly.
  const [estimate, setEstimate] = useState<DiscoveredEstimate | null>(null);
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);
  const localEstimate = useMemo(() => {
    let pages = 0; let cost = 0; let unknown = 0;
    for (const r of selectedRows) {
      const p = r.page_count ?? r.estimated_pages;
      if (p == null) unknown += 1;
      else { pages += p; cost += r.estimated_cost_usd; }
    }
    return { count: selectedRows.length, estimated_pages: pages, estimated_cost_usd: cost, unknown_size_rows: unknown };
  }, [selectedRows]);
  useEffect(() => {
    if (selected.size === 0) { setEstimate(null); return; }
    let cancelled = false;
    api.estimateDiscovered(Array.from(selected))
      .then((e) => { if (!cancelled) setEstimate(e); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selected]);
  const shown = estimate ?? localEstimate;

  // Per-tab forecast: total pages + cost across ALL rows on the current tab
  // (not just the selection). Master/admin view of "what would converting
  // everything I see cost?".
  const tabEstimate = useMemo(() => {
    let pages = 0; let cost = 0; let unknown = 0;
    for (const r of rows) {
      const p = r.page_count ?? r.estimated_pages;
      if (p == null) unknown += 1;
      else { pages += p; cost += r.estimated_cost_usd; }
    }
    return { count: rows.length, pages, cost, unknown };
  }, [rows]);

  return (
    <div>
      <PageHeader
        title="Discovered PDFs"
        icon={<Inbox className="h-6 w-6 text-brand-600" aria-hidden="true" />}
        description="Pick which PDFs from the crawl to convert. Nothing converts automatically."
        actions={<button onClick={refresh} className="btn-secondary"><RefreshCw className="h-4 w-4" aria-hidden="true" /> Refresh</button>}
      />

      {/* Status tabs */}
      <div className="border-b border-slate-200 mb-4 flex gap-1 overflow-x-auto" role="tablist" aria-label="Filter by status">
        {STATUS_TABS.map((t) => {
          const active = filters.status_filter === t.key;
          const n = t.key === "all"
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : (counts[t.key] || 0);
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setStatus(t.key)}
              className={
                "px-3 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 " +
                (active
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-slate-500 hover:text-slate-700")
              }
            >
              {t.label} {t.key !== "all" && <span className="ml-1 text-xs text-slate-400">{n}</span>}
            </button>
          );
        })}
      </div>

      {/* Filter row */}
      <form onSubmit={applySearch} className="filter-bar" role="search">
        <div className="filter-grow">
          <label htmlFor="disc-search" className="field-label">Search file name or URL</label>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="disc-search"
              className="input pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="agenda, budget, permit…"
            />
          </div>
        </div>
        <div>
          <label htmlFor="disc-site" className="field-label">Site</label>
          <select
            id="disc-site"
            className="input"
            value={filters.site_id || ""}
            onChange={(e) => setFilters((f) => ({ ...f, site_id: e.target.value || undefined }))}
          >
            <option value="">All sites</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.domain}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:contents">
        <div>
          <label htmlFor="disc-after" className="field-label">Discovered after</label>
          <input
            id="disc-after"
            type="date"
            className="input"
            value={filters.after?.slice(0, 10) || ""}
            onChange={(e) => setFilters((f) => ({ ...f, after: e.target.value ? new Date(e.target.value).toISOString() : undefined }))}
          />
        </div>
        <div>
          <label htmlFor="disc-before" className="field-label">Before</label>
          <input
            id="disc-before"
            type="date"
            className="input"
            value={filters.before?.slice(0, 10) || ""}
            onChange={(e) => setFilters((f) => ({ ...f, before: e.target.value ? new Date(e.target.value).toISOString() : undefined }))}
          />
        </div>
        </div>
        <div className="flex gap-2">
          <button type="submit" className="btn-secondary flex-1 sm:flex-none">Apply</button>
          {(filters.q || filters.site_id || filters.after || filters.before) && (
            <button type="button" className="btn-ghost" onClick={() => { setFilters({ status_filter: filters.status_filter }); setSearch(""); }}>Clear</button>
          )}
        </div>
      </form>

      {/* Total-when-filtered — surfaced at the top of the table so users
          always see the size of the current filtered set (the pagination
          footer shows the same number but is off-screen for long tables). */}
      {!loading && (
        <div className="mb-3 text-sm text-slate-600 flex items-center flex-wrap gap-x-3 gap-y-1">
          <span>
            <strong className="text-ink tabular-nums">{total.toLocaleString()}</strong>{" "}
            PDF{total === 1 ? "" : "s"}
            {(filters.q || filters.site_id || filters.after || filters.before || filters.status_filter !== "discovered") && (
              <span className="text-slate-400"> matching current filters</span>
            )}
          </span>
          {rows.length > 0 && rows.length < total && (
            <span className="text-xs text-slate-500">
              showing {rows.length} on this page
            </span>
          )}
        </div>
      )}

      {/* Forecast banner — page count for everyone, cost super-admin-only. */}
      {rows.length > 0 && tabEstimate.pages > 0 && (
        <div className="mb-4 text-xs text-slate-600 flex items-center gap-2">
          <DollarSign className="h-3.5 w-3.5 text-slate-400" />
          <span>
            Forecast for this tab: <strong>{tabEstimate.pages.toLocaleString()}</strong> pages
            {isSuper && (
              <> · <strong className="ml-0.5">{formatUsd(tabEstimate.cost)}</strong></>
            )}
            {tabEstimate.unknown > 0 && (
              <span className="text-slate-400"> · {tabEstimate.unknown} row{tabEstimate.unknown === 1 ? "" : "s"} unknown size</span>
            )}
          </span>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="card bg-brand-50 border-brand-200 mb-4">
          <div className="card-body py-3 flex flex-wrap items-center gap-3">
            <strong className="text-brand-900">{selected.size} selected</strong>
            <span className="text-xs text-brand-900/70 inline-flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5" />
              {shown.estimated_pages.toLocaleString()} pages
              {isSuper && <> · {formatUsd(shown.estimated_cost_usd)}</>}
              {shown.unknown_size_rows > 0 && (
                <span className="text-slate-500"> ({shown.unknown_size_rows} unknown size)</span>
              )}
            </span>
            {!demo && <>
              <button onClick={convertSelected} className="btn-primary"><FileCheck2 className="h-4 w-4" /> Convert</button>
              <button onClick={ignoreSelected} className="btn-secondary"><X className="h-4 w-4" /> Ignore</button>
            </>}
            <button onClick={() => setSelected(new Set())} className="btn-ghost ml-auto">Clear selection</button>
          </div>
        </div>
      )}

      {confirmNode}

      {error && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="card-body text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="card-body text-sm text-slate-500 flex items-center gap-2">
            <Inbox className="h-4 w-4 text-slate-400" />
            Nothing here yet. Go to the Sites tab and click <strong>Crawl</strong>.
          </div>
        ) : (
          <div className="table-wrap">
          {/* Narrower screens fold Source, Site, Size and date (below 2xl) and
              Pages, cost and Status (below md) into the file cell. */}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </th>
                <th className="text-left px-3 py-3">
                  <SortHeader<DiscoveredSortField>
                    label="Filename" field="filename"
                    current={sort} order={order} onChange={onSort}
                  />
                </th>
                <th className="hidden 2xl:table-cell text-left px-3 py-3 w-[110px]" title="Where this URL came from">
                  <SortHeader<DiscoveredSortField>
                    label="Source" field="source"
                    current={sort} order={order} onChange={onSort}
                  />
                </th>
                <th className="hidden 2xl:table-cell text-left px-3 py-3 w-[140px]">Site</th>
                <th className="hidden 2xl:table-cell text-left px-3 py-3 w-[80px]">
                  <SortHeader<DiscoveredSortField>
                    label="Size" field="content_length"
                    current={sort} order={order} onChange={onSort}
                  />
                </th>
                <th className="hidden md:table-cell text-left px-3 py-3 w-[64px] whitespace-nowrap" title="Real page count from the PDF (captured at crawl)">
                  <SortHeader<DiscoveredSortField>
                    label="Pages" field="page_count"
                    current={sort} order={order} onChange={onSort}
                  />
                </th>
                {isSuper && <th className="hidden md:table-cell text-left px-3 py-3 w-[88px] whitespace-nowrap" title="Estimated conversion cost at Haiku 4.5 pricing ($1 / $5 per 1M tokens)">Est. cost</th>}
                <th className="hidden 2xl:table-cell text-left px-3 py-3 w-[110px] whitespace-nowrap">
                  <SortHeader<DiscoveredSortField>
                    label="Last modified" field="last_modified"
                    current={sort} order={order} onChange={onSort}
                  />
                </th>
                <th className="hidden md:table-cell text-left px-3 py-3 w-[100px]">
                  <SortHeader<DiscoveredSortField>
                    label="Status" field="status"
                    current={sort} order={order} onChange={onSort}
                  />
                </th>
                <th className="text-right px-3 py-3 w-[1%]"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.map((r) => (
                <tr key={r.id} className={selected.has(r.id) ? "bg-brand-50/50" : undefined}>
                  <td className="px-3 py-3 align-top">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                      aria-label={`Select ${r.filename}`}
                    />
                  </td>
                  <td className="px-3 py-3 max-w-0 min-w-[14rem] align-top">
                    <div className="font-medium text-slate-800 truncate flex items-center gap-1.5" title={r.filename}>
                      <ScanIndicator status={r.scan_status} error={r.scan_error} />
                      <span className="truncate">{r.filename}</span>
                    </div>
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 truncate block hover:underline" title={r.url}>{r.url}</a>
                    {r.found_on && (
                      <a href={r.found_on} target="_blank" rel="noopener noreferrer" className="text-[11px] text-slate-400 truncate block hover:underline" title={`Found on ${r.found_on}`}>
                        found on {r.found_on.replace(/^https?:\/\//, "")}
                      </a>
                    )}
                    {r.linking_pages && r.linking_pages.length > 1 && (
                      <details className="text-[11px] text-slate-500 mt-0.5">
                        <summary className="cursor-pointer hover:text-slate-700">
                          linked from {r.linking_pages.length} page{r.linking_pages.length === 1 ? "" : "s"}
                        </summary>
                        <ul className="mt-1 pl-3 space-y-0.5">
                          {r.linking_pages.slice(0, 20).map((lp, i) => (
                            <li key={i} className="truncate">
                              <a href={lp.url} target="_blank" rel="noopener noreferrer" className="hover:underline text-slate-500" title={lp.url}>
                                {lp.title || lp.url.replace(/^https?:\/\//, "")}
                              </a>
                            </li>
                          ))}
                          {r.linking_pages.length > 20 && (
                            <li className="text-slate-400">…and {r.linking_pages.length - 20} more</li>
                          )}
                        </ul>
                      </details>
                    )}
                    <div className="2xl:hidden mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                      <span className="md:hidden"><span className={`pill-${r.status}`}>{r.status}</span></span>
                      <SourceChip source={r.source} />
                      <span className="truncate max-w-full">{siteName(r.site_id)}</span>
                      <span className="tabular-nums">{formatBytes(r.content_length)}</span>
                      <span className="md:hidden tabular-nums">
                        {r.page_count ?? (r.estimated_pages != null ? `~${r.estimated_pages}` : "?")} pages
                        {isSuper && r.estimated_pages != null && <> · {formatUsd(r.estimated_cost_usd)}</>}
                      </span>
                      {r.last_modified && <span>modified {new Date(r.last_modified).toLocaleDateString()}</span>}
                    </div>
                  </td>
                  <td className="hidden 2xl:table-cell px-3 py-3 align-top">
                    <SourceChip source={r.source} />
                  </td>
                  <td className="hidden 2xl:table-cell px-3 py-3 text-slate-600 max-w-[140px] align-top">
                    <div className="truncate" title={siteName(r.site_id)}>{siteName(r.site_id)}</div>
                  </td>
                  <td className="hidden 2xl:table-cell px-3 py-3 text-slate-600 tabular-nums align-top">{formatBytes(r.content_length)}</td>
                  <td className="hidden md:table-cell px-3 py-3 text-slate-600 tabular-nums align-top">
                    {r.page_count != null
                      ? r.page_count
                      : r.estimated_pages != null
                        ? <span className="text-slate-400" title="Approximate — PDF couldn't be parsed at crawl time">~{r.estimated_pages}</span>
                        : <span className="text-slate-400">—</span>}
                  </td>
                  {isSuper && (
                    <td className="hidden md:table-cell px-3 py-3 text-slate-600 tabular-nums align-top">
                      {r.estimated_pages != null
                        ? formatUsd(r.estimated_cost_usd)
                        : <span className="text-slate-400">—</span>}
                    </td>
                  )}
                  <td className="hidden 2xl:table-cell px-3 py-3 text-slate-500 align-top">{r.last_modified ? new Date(r.last_modified).toLocaleDateString() : "—"}</td>
                  <td className="hidden md:table-cell px-3 py-3 align-top"><span className={`pill-${r.status}`}>{r.status}</span></td>
                  <td className="px-3 py-3 align-top">
                    <div className="flex flex-row flex-nowrap items-center justify-end gap-1">
                      {r.status === "discovered" && !demo && (
                        <button
                          onClick={() => convertIds([r.id])}
                          className="btn-secondary !px-2 !py-1.5 text-xs"
                          title="Convert this PDF"
                          aria-label={`Convert ${r.filename}`}
                        >
                          <FileCheck2 className="h-3.5 w-3.5" aria-hidden="true" />
                          <span className="hidden sm:inline">Convert</span>
                        </button>
                      )}
                      {!demo && <button
                        onClick={() => deleteRow(r.id, r.filename)}
                        className="btn-ghost text-rose-600 !px-2 !py-1.5"
                        aria-label={`Delete ${r.filename}`}
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {!loading && total > 0 && (
          <Pagination
            page={page}
            perPage={perPage}
            total={total}
            onPage={setPage}
            onPerPage={setPerPage}
          />
        )}
      </div>
    </div>
  );
}

function formatBytes(n: number | null): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ScanIndicator({ status, error }: { status: Discovered["scan_status"]; error: string | null }) {
  if (status === "scanned") {
    return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" aria-label="Scanned" />;
  }
  if (status === "failed") {
    return (
      <span title={error || "Scan failed"} className="inline-flex">
        <AlertTriangle
          className="h-3.5 w-3.5 text-rose-500 shrink-0"
          aria-label={error ? `Scan failed: ${error}` : "Scan failed"}
        />
      </span>
    );
  }
  return <Loader2 className="h-3.5 w-3.5 text-slate-400 animate-spin shrink-0" aria-label="Scanning…" />;
}

const SOURCE_LABEL: Record<Discovered["source"], { label: string; cls: string }> = {
  "wp-plugin": { label: "WP plugin",  cls: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
  "wp-rest":   { label: "WP REST",    cls: "bg-indigo-50 text-indigo-600 ring-indigo-200" },
  "sitemap":   { label: "sitemap",    cls: "bg-sky-50 text-sky-700 ring-sky-200" },
  "bfs":       { label: "crawl",      cls: "bg-slate-50 text-slate-700 ring-slate-200" },
  "selenium":  { label: "JS render",  cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  "robots":    { label: "robots",     cls: "bg-slate-50 text-slate-500 ring-slate-200" },
  "tracker":   { label: "visitor",    cls: "bg-violet-50 text-violet-700 ring-violet-200" },
  "crawl":     { label: "crawl",      cls: "bg-slate-50 text-slate-600 ring-slate-200" },
};

function SourceChip({ source }: { source: Discovered["source"] }) {
  const meta = SOURCE_LABEL[source] ?? SOURCE_LABEL.crawl;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium rounded ring-1 ring-inset ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function formatUsd(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "$0.00";
  if (n < 0.01) return `<$0.01`;
  if (n < 1) return `$${n.toFixed(2)}`;
  if (n < 100) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(0)}`;
}
